import type { ErrorEvent } from '@sentry/nextjs';

// Guards for noise from code that is not ours (rrweb, Google Places, an injected
// page harness). Each backs one `dropEvent` branch in sentry.client.config.ts.

/**
 * rrweb's DOM serializer, bundled by BOTH session recorders this app loads,
 * warns when it cannot read an inline `<style>` element's `sheet.cssRules`.
 * The catch is gated on `parentTagName === "STYLE"`, so it fires for the text
 * node inside an INLINE `<style>` whose sheet is not origin-clean (in practice
 * an extension-injected `<style>`, or one inside a `blob:`/`srcdoc` frame) —
 * never for a `<link rel=stylesheet>` to a CDN. Which origin injected it was
 * not determined; if it were ever one of OUR `<style>` blocks the right
 * response would be a replay fix, not this guard. The upstream template is
 * verbatim:
 *
 *   catch (err) {
 *     console.warn(`Cannot get CSS styles from text's parentNode. Error: ${err}`, textNode);
 *   }
 *
 * It is a CATCH block that then continues with the element's AUTHORED
 * textContent, so the recorder recovers on its own — the only cost is that the
 * replay shows that one sheet's authored CSS instead of its live CSSOM, so
 * rules added or removed at runtime are misrepresented. Nothing on the page is
 * broken, which matches the production signal: BALLEE-HG is 7 events / 0 users
 * affected on /:handle, Firefox Mobile on Android, the warning repeating within
 * a single pageload as the recorder re-serializes the DOM.
 *
 * This is a closed observability loop, not a product defect. We register BOTH
 * `Sentry.replayIntegration()` — whose @sentry/replay build ships this exact
 * string in its executable ESM and CJS, not merely in a sourcemap — AND
 * `captureConsoleIntegration({ levels: ['error', 'warn'] })`, in
 * apps/web/instrumentation-client.ts. Our recorder warns; our own console
 * integration captures that warning and reports it back to Sentry as an issue.
 * posthog-js bundles the same rrweb code and can emit it too. The sentence
 * appears NOWHERE in apps/ or packages/ — verified by grep, so it can only ever
 * be third-party recorder output.
 *
 * WHY A STRUCTURAL GUARD AND NOT AN `IGNORED_ERROR_PATTERNS` ENTRY: an entry
 * there would be dead code for this class. Verified against @sentry/core@10.70.0
 * — `console.warn(string, textNode)` has no argument that is `instanceof Error`,
 * so `captureConsoleIntegration` takes its `captureMessage` branch, which builds
 * the hint as `{ event_id, ...{ captureContext, syntheticException } }` and
 * NEVER sets `originalException`. `addExceptionMechanism` then no-ops because
 * there is no `event.exception`, so no `mechanism` is stamped either. The text
 * survives ONLY on `event.message`, while `IGNORED_ERROR_PATTERNS` is matched
 * against `hint.originalException` and `event.exception[0].value` — both empty
 * here. The production event confirms the shape: type `default`, no exception
 * entry, tags `logger=console` and `level=warning`.
 *
 * The guard is four conjunctive structural conditions, none of which a genuine
 * application failure satisfies:
 *   1. `logger === 'console'` — stamped unconditionally by captureConsole; a
 *      thrown application error never carries it.
 *   2. `level === 'warning'`  — console.warn only. An error-level capture of the
 *      same text is KEPT.
 *   3. no `event.exception`   — a real failure carries one, so this can never be
 *      a swallowed crash.
 *   4. the message STARTS with the verbatim upstream sentence.
 *
 * Deliberately narrower than a bare message match: it cannot swallow a
 * Postgres/Supabase-outage symptom (those are exceptions, and the
 * cloudflare-origin-timeout block in beforeSend KEEPS them before this runs),
 * and it cannot swallow a first-party error that merely quotes the sentence.
 * See BALLEE-HG.
 */
export const RRWEB_CSS_SERIALIZER_WARNING =
  /^cannot get css styles from text's parentnode\. error:/i;

export function isRrwebCssSerializerWarning(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  if (event.level !== 'warning') return false;
  if (event.exception?.values?.length) return false;
  const message = typeof event.message === 'string' ? event.message : '';
  return RRWEB_CSS_SERIALIZER_WARNING.test(message);
}

/**
 * Google Maps Places telling us the id it just handed us has been superseded
 * (BALLEE-JE / BALLEE-JH).
 *
 * The emitter, read from the live bundle Google serves rather than inferred —
 * `maps-api-v3/api/js/66/4d/places.js`, reached from the loader URL below:
 *
 *   Lzb = function (a, b) {
 *     b = _.Vn(b);
 *     a.id !== b && b !== "" && (a.id !== "" &&
 *       console.warn(`Please note that the 'id' property of this place has changed: ${a.id} -> ${b}`),
 *       a.Eh = b, a.Pi = `places/${b}`, ...)
 *   }
 *
 * Two things that decide the fix are visible there. `console.warn` receives
 * exactly ONE argument, a template-literal string — no Error, so the capture
 * takes the message branch (below). And the line AFTER the warn assigns the new
 * id (`a.Eh = b`) and rewrites the resource name: the SDK self-heals and carries
 * on. The sentence annotates a success, not a failure.
 *
 * Google documents the behaviour as routine: "Place IDs may change over time",
 * and the remedy is to refresh a stored id, "at no charge"
 * (developers.google.com/maps/documentation/javascript/place-id). The genuine
 * error condition is a different one — `NOT_FOUND` on an obsolete id.
 *
 * WHY THIS CLASS IS CORROSIVE RATHER THAN MERELY NOISY. The sentence embeds
 * BOTH place ids, and a place id is unique per address, so the message text is
 * unique per address. Sentry groups a message capture by its text, so every
 * distinct address a user types mints a BRAND NEW issue. Two appeared on
 * 2026-09-11 alone (a Berlin street and a Poznan one), each 1 event / 1 user.
 * This is unbounded group creation driven by user input — the reason it is
 * worth a guard at 1 event per group.
 *
 * IS IT TELLING US OUR STORED DATA IS STALE? No, and this was checked before
 * the guard was written, because silencing a staleness warning would be the
 * wrong fix and would be invisible afterwards. Three findings, all first-party
 * source:
 *
 *   1. On this route the id is fresh by construction. The breadcrumb trail on
 *      both events is `ui.input` -> `AutocompletePlaces` -> `GetPlace` ->
 *      this warning, the last two 2ms and 11ms apart. The id handed to
 *      `GetPlace` came from the autocomplete response SECONDS earlier in the
 *      same interaction. Google is superseding an id between its OWN two
 *      calls.
 *   2. `/home/artist/contract-details` never re-queries a stored id anyway.
 *      Its hydration hook (`use-contract-details.ts`) restores
 *      `streetAddress`, `city`, `postalCode` and friends and does NOT restore
 *      `placeId`, which stays undefined; typing re-clears it.
 *   3. `use-places.ts:437` returns `placeId: place.id ?? placeId` — it ADOPTS
 *      Google's replacement id, which is what the notice asks for.
 *
 * STORED IDS ARE RE-QUERIED ELSEWHERE, and that is where this guard hides
 * something. `LocationAutocomplete` (apps/web/app/home/(user)/_components/)
 * offers a hydrated id as a selectable option and passes it to
 * `new Place({ id })`, and its result is saved again: artist locations
 * (`add-location-dialog.tsx` -> `artist_locations.place_id`) and marketplace
 * listings (`step-price-delivery.tsx` -> `location_place_id`). Until
 * 2026-09-12 it re-persisted the REQUESTED id, so a superseded id survived
 * every save — and this guard, which applies on every route, hid the only
 * signal of it. The same review changed it to request the `'id'` field and
 * adopt `place.id`, like `use-places.ts`. Both halves are needed: the SDK only
 * fetches the canonical id when `'id'` is requested, because the constructor
 * marks the id as known. A superseded stored id now corrects itself on the next
 * selection. `address-lookup.tsx` (venue `google_place_id`) got the same change,
 * but it only ever resolves FRESH autocomplete ids, so there it covers an id
 * Google replaces within one interaction. All THREE call sites are now pinned
 * by tests that go red if either `'id'` or the adoption is removed:
 * `location-autocomplete.test.ts`, `address-lookup.test.tsx`, and
 * `use-places-place-id.test.ts` — the last added after BALLEE-JP, because
 * `use-places.ts` is the call site on `/home/artist/contract-details`, the very
 * route all three issues came from, and it was the one with no test. Any new
 * `new Place({ id })` call site must do the same: this guard makes the reminder
 * invisible.
 *
 * VERIFIED THIRD-PARTY, NOT OURS. The sentence appears in NO first-party file
 * (`apps`, `packages`, `tools`) and in NO installed package — both greps run
 * with a control that returned non-zero on the same invocation, so the zeros
 * measure the tree and not a broken command. It could not be bundled in any
 * case: `packages/features/artists/src/lib/google-maps-loader.ts:85` injects
 * `<script src="https://maps.googleapis.com/maps/api/js?...">` at runtime, so
 * the Places code is fetched from Google on every pageload and lives nowhere
 * in this repo. Re-verify with
 * `grep -rls "property of this place has changed" node_modules/.pnpm | grep -v '\.map$'`
 * — expect zero, and pair it with a control that is expected to hit.
 *
 * WHY A STRUCTURAL GUARD AND NOT AN `IGNORED_ERROR_PATTERNS` ENTRY: an entry
 * there would be dead code for this class, for exactly the reason documented on
 * the rrweb guard above. Re-verified against the INSTALLED
 * `@sentry/core@10.70.0` rather than assumed —
 * `build/cjs/integrations/captureconsole.js` does
 * `const error = args.find((arg) => arg instanceof Error)` and only then
 * `captureException`; otherwise it takes `scope.captureMessage(safeJoin(args))`,
 * which never sets `hint.originalException`. `addExceptionMechanism` then
 * no-ops for want of an `event.exception`, so no mechanism is stamped either.
 * The ignore list is matched against `hint.originalException` and
 * `event.exception[0].value` — both empty here.
 *
 * The two production events confirm that shape rather than merely agreeing with
 * it: type `default`, zero exception entries, no mechanism, `logger=console`,
 * `level=warning`, and `extra.arguments` of length 1 — so Google passes the
 * sentence as the ONLY console argument and it sits at offset 0.
 *
 * Four conjunctive structural conditions, none of which a genuine application
 * failure satisfies:
 *   1. `logger === 'console'` — stamped unconditionally by captureConsole.
 *   2. `level === 'warning'`  — an error-level capture of the same text is KEPT.
 *   3. no `event.exception`   — a real failure carries one, so this can never
 *      swallow a crash.
 *   4. the message STARTS with the verbatim sentence.
 *
 * `^`-anchored deliberately, and stopping at the colon: everything after it is
 * the two ids and varies per address. Without the anchor, any error merely
 * CONTAINING the sentence would be discarded, which an attacker who can get 61
 * chosen characters into a client error message could use to suppress a real
 * error class.
 */
export const GOOGLE_PLACES_ID_CHANGED_NOTICE =
  /^please note that the 'id' property of this place has changed:/i;

/**
 * Stand-in reported to the drop telemetry instead of the real console text.
 *
 * The real sentence carries two place ids, and a place id is base64url that
 * DECODES TO THE ADDRESS — the Berlin event's id decodes to
 * "Martin-Luther-Strasse, Berlin-Tempelhof-Schoeneberg, Germany". `messagePrefix`
 * is 120 chars, so the real text would carry most of one id into PostHog.
 * `buildDropInfo` redacts by PATTERN and none of its rules match base64url
 * (checked: the email, uuid, jwt, AWS-key, phone and IBAN rules all miss it),
 * so it would arrive unredacted. The class is fully identified by `reason`, and
 * `route` already carries the normalized path, so the text buys nothing and
 * leaks a user's typed address. Same reasoning as
 * `RSC_PREFETCH_FALLBACK_DROP_VALUE` in console-capture-guards.ts.
 */
export const GOOGLE_PLACES_ID_CHANGED_DROP_VALUE =
  "Please note that the 'id' property of this place has changed: <place-ids omitted>";

export function isGooglePlacesIdChangedNotice(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  if (event.level !== 'warning') return false;
  if (event.exception?.values?.length) return false;
  const message = typeof event.message === 'string' ? event.message : '';
  return GOOGLE_PLACES_ID_CHANGED_NOTICE.test(message);
}

/**
 * An injected page harness that wraps `addEventListener` and throws inside its
 * own hook (BALLEE-HQ / BALLEE-HR / BALLEE-HS).
 *
 * A crawler loads the page inside an instrumented browser. That harness logs
 * every hook it fires as a tab-separated `[jshost]<TAB><hook><TAB><base64>`
 * console line, and replaces the top-level `addEventListener`. Its replacement
 * reads `.tagName` off a receiver it never checked, and throws. The SAME throw
 * reaches Sentry three times over — once through `captureConsoleIntegration`
 * (a MESSAGE event, so there is no `event.exception`), once through `onerror`,
 * once through `onunhandledrejection` — which is why one defect occupies three
 * issues.
 *
 * Evidence it is not our code. All 12 events of all three issues, 2026-08-28:
 *   - the throw site is `addEL_hook` at filename `<anonymous>`, and no
 *     first-party frame appears in any of the stacks;
 *   - the harness's own `onerror` breadcrumb reports the failure at ITS OWN
 *     line 675, exactly where its `addEL_hook` frame sits;
 *   - one decoded breadcrumb payload is Sentry's own history-instrumentation
 *     closure, so the harness is wrapping our SDK from outside it;
 *   - `User-Agent: pc` with an `m.baidu.com` search referrer, and 0 affected
 *     users — no human session;
 *   - the same throw lands on `(adaptive)/[handle]` and on `/auditions/[slug]`,
 *     two structurally unrelated route trees, so it is not page-specific.
 *
 * WHY A STRUCTURAL GUARD AND NOT AN `IGNORED_ERROR_PATTERNS` ENTRY: the text is
 * generic, and first-party code does read `.tagName`. A message pattern would
 * silence a genuine first-party dereference of the same shape forever.
 * `isExternalScriptError` cannot see it either — that matches on `filename`,
 * and the harness is not served from any URL, so its frames carry the literal
 * `<anonymous>` and there is no path to add to the list.
 *
 * So this requires BOTH halves: the harness's own marker, AND the single
 * message it is known to throw. Either alone is too wide — the marker alone
 * would swallow every unrelated error occurring during a crawl, and the
 * message alone would swallow the first-party dereference described above.
 */
const PAGE_HARNESS_CONSOLE_MARKER = '[jshost]\t';
const PAGE_HARNESS_THROW_FUNCTION = 'addEL_hook';
const PAGE_HARNESS_ANONYMOUS_FILENAME = '<anonymous>';

/** Stable grouping key for the class. Renaming it splits its history in two. */
export const PAGE_HARNESS_PATTERN_SLUG = 'page-harness-addeventlistener-hook';

const PAGE_HARNESS_TAGNAME_READ =
  /cannot read properties of null \(reading 'tagname'\)/i;

/** The harness announces every hook it fires on the console before it throws. */
function hasPageHarnessBreadcrumb(event: ErrorEvent): boolean {
  return (event.breadcrumbs ?? []).some(
    (crumb) =>
      typeof crumb?.message === 'string' &&
      crumb.message.startsWith(PAGE_HARNESS_CONSOLE_MARKER),
  );
}

/** ...and owns the throw site, in a script that has no URL of its own. */
function hasPageHarnessFrame(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some(
    (frame) =>
      frame?.function === PAGE_HARNESS_THROW_FUNCTION &&
      frame?.filename === PAGE_HARNESS_ANONYMOUS_FILENAME,
  );
}

export function isPageHarnessAddEventListenerHook(event: ErrorEvent): boolean {
  if (!hasPageHarnessBreadcrumb(event) && !hasPageHarnessFrame(event)) {
    return false;
  }
  const message = typeof event.message === 'string' ? event.message : '';
  const exceptionValue = event.exception?.values?.[0]?.value ?? '';
  return PAGE_HARNESS_TAGNAME_READ.test(`${message}\n${exceptionValue}`);
}
